import React, { useState } from "react";
import { Bot, Send, Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function ResultSummary({ toolResults }) {
  if (!toolResults?.length) return null;
  return (
    <details className="text-xs text-slate-500">
      <summary className="cursor-pointer py-1">Technikai adatok ({toolResults.length})</summary>
      <pre className="mt-2 p-2 rounded-lg bg-black/30 overflow-x-auto max-h-40 text-[10px]">
        {JSON.stringify(toolResults, null, 2)}
      </pre>
    </details>
  );
}

export function AdminAssistant({ onMutate, onSiteContentSaved }) {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastResults, setLastResults] = useState([]);
  const [pending, setPending] = useState([]);
  const [executingId, setExecutingId] = useState(null);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setLoading(true);
    setPending([]);
    setLastResults([]);
    const userTurn = { role: "user", content: text };
    setHistory((h) => [...h, userTurn]);
    setInput("");
    try {
      const r = await api.post("/admin/assistant/chat", {
        message: text,
        history: history.slice(-20),
      });
      const msg = r.data.assistant_message || "Kész.";
      setHistory((h) => [...h, { role: "assistant", content: msg }]);
      setLastResults(r.data.tool_results || []);
      setPending(r.data.pending_actions || []);
    } catch (e) {
      const detail = e.response?.data?.detail || "Asszisztens nem elérhető";
      toast.error(detail);
      setHistory((h) => [...h, { role: "assistant", content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async (action) => {
    setExecutingId(action.id);
    try {
      const r = await api.post("/admin/assistant/execute", { action });
      toast.success("Művelet végrehajtva");
      setPending((p) => p.filter((a) => a.id !== action.id));
      if (action.type?.includes("approve") || action.type === "adjust_user_credits") {
        onMutate?.();
      }
      if (action.type === "apply_site_content") {
        onSiteContentSaved?.();
      }
      setHistory((h) => [
        ...h,
        { role: "assistant", content: `✓ ${action.summary}\n${JSON.stringify(r.data.result)}` },
      ]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sikertelen");
    } finally {
      setExecutingId(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="admin-assistant">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-electric/20 flex items-center justify-center shrink-0">
          <Bot className="w-5 h-5 text-electric" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-light">Admin asszisztens</h2>
          <p className="text-sm text-slate-400 mt-1">
            Kérdezhetsz (látogatók, megtekintések, profit), jóváhagyhatsz tömegesen, kreditet módosíthatsz,
            weboldalt szerkeszthetsz — az írási műveleteknél megerősítés kell.
          </p>
        </div>
      </div>

      <div className="glass rounded-2xl border border-white/10 min-h-[420px] flex flex-col">
        <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[480px]">
          {history.length === 0 && (
            <div className="text-xs text-slate-500 space-y-2 leading-relaxed">
              <p>Példák:</p>
              <p>• „Hányan vannak most a weboldalon és mennyi az összes autó megtekintés?”</p>
              <p>• „Hány autó vár jóváhagyásra? Hagyd jóvá mindet.”</p>
              <p>• „Adj 100 kreditet user@email.com-nak — kompenzáció miatt”</p>
              <p>• „A hero szöveget tedd rövidebbre, említsd a 360°-t”</p>
            </div>
          )}
          {history.map((m, i) => (
            <div
              key={i}
              className={`rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-white/[0.06] ml-6" : "bg-electric/10 mr-6 text-slate-200"
              }`}
            >
              {m.content}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <Loader2 className="w-3 h-3 animate-spin" /> Dolgozik…
            </div>
          )}
          <ResultSummary toolResults={lastResults} />
        </div>

        {pending.length > 0 && (
          <div className="px-4 py-3 border-t border-amber-500/20 bg-amber-500/5 space-y-2">
            <div className="flex items-center gap-2 text-xs text-amber-200 uppercase tracking-wide">
              <ShieldAlert className="w-3.5 h-3.5" /> Megerősítés szükséges
            </div>
            {pending.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-400/20 px-3 py-2"
              >
                <div className="text-sm text-slate-200">{a.summary}</div>
                {a.preview_note && (
                  <p className="text-xs text-slate-500 w-full">{a.preview_note}</p>
                )}
                <button
                  type="button"
                  data-testid={`admin-assistant-confirm-${a.id}`}
                  disabled={executingId === a.id}
                  onClick={() => executeAction(a)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 text-anthracite-900 text-xs font-medium disabled:opacity-50"
                >
                  {executingId === a.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3 h-3" />
                  )}
                  Végrehajtás
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="p-3 border-t border-white/10 flex gap-2">
          <textarea
            data-testid="admin-assistant-input"
            value={input}
            disabled={loading}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Írd meg, mit szeretnél tudni vagy csinálni…"
            className="flex-1 resize-none rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white"
          />
          <button
            type="button"
            data-testid="admin-assistant-send"
            disabled={loading || !input.trim()}
            onClick={send}
            className="self-end px-4 py-2.5 rounded-xl bg-electric text-white disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
